/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserAuthenticationResult } from '@/types';
import { useAppStore } from './appStore';

interface AuthState {
  user: UserAuthenticationResult | null;
  token: string | null;
  isAdmin: boolean;
  isOwner: boolean;
  isPartner: boolean;
  isAuthenticated: boolean;
  setAuth: (user: UserAuthenticationResult, token: string) => void;
  /** Set only the JWT token (for authKey-based pages like create-user-and-agreement) */
  setTokenOnly: (token: string) => void;
  /** Merge partial fields into the persisted user (e.g. after a profile edit). */
  patchUser: (partial: Partial<UserAuthenticationResult>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAdmin: false,
      isOwner: false,
      isPartner: false,
      isAuthenticated: false,
      setAuth: (user: UserAuthenticationResult, token: string) =>
        set((state) => {
          // The app store (selectedOwner above all) is persisted separately and survives a session
          // that ended without Logout — an expired token, a closed tab. Signing in as someone else
          // then inherited the previous user's selected owner: an owner signing in on a browser an
          // admin had used acted on another owner's id, got 403 on their own toll accounts, and the
          // Toll Accounts tab showed an empty provider list. Keep it only when the same user signs in again.
          if (!state.user || state.user.id !== user.id) {
            useAppStore.getState().reset();
          }
          return {
            user,
            token,
            isAdmin: user.isAdmin,
            isOwner: user.isOwner,
            isPartner: user.isPartner,
            isAuthenticated: true,
          };
        }),
      setTokenOnly: (token: string) =>
        set({ token, isAuthenticated: true }),
      patchUser: (partial: Partial<UserAuthenticationResult>) =>
        set((state) =>
          state.user ? { user: { ...state.user, ...partial } } : {}),
      logout: () => {
        set({
          user: null,
          token: null,
          isAdmin: false,
          isOwner: false,
          isPartner: false,
          isAuthenticated: false,
        });
        localStorage.removeItem('aegis-auth-storage');
        localStorage.removeItem('aegis-app-storage');
      },
    }),
    {
      name: 'aegis-auth-storage',
    }
  )
);
